'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { Button } from '@eliadmin/core/src/components/ui/button';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@eliadmin/core/src/components/ui/sheet';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { IoMdCart } from 'react-icons/io';
import { LanguageSelect } from './language-select';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

const CategoryItem = ({ category, layer }: { category: CategoriesEntity; layer?: number }) => {
  const hasChildren = !!category.childs?.length;

  return (
    <AccordionItem
      className={cn('flex flex-col', layer && 'ml-2 font-semibold')}
      value={category.slug + layer?.toString()}
    >
      <li
        className={cn(
          'flex w-full flex-1 flex-row items-center justify-between overflow-hidden whitespace-normal px-3 py-1 last:border-r-0',
          'ea-menu-sidebar__list-item',
          // className?.listItem,
        )}
      >
        <SheetClose asChild>
          <Link href={category.slug} className="flex-1" prefetch={false}>
            <span
              data-ea-visual-tag="button"
              style={{ display: 'inline-block' }}
              className={cn(
                'text-left text-xl font-semibold',
                layer === 1 && 'text-[16px] text-lg font-medium',
                layer && layer > 1 && 'text-sm font-normal',
              )}
            >
              {category.name}
            </span>
          </Link>
        </SheetClose>
        {hasChildren && (
          <AccordionTrigger aria-label={category.name} className="h-5 w-5" iconstyles="w-5 h-5 text-primary flex-1" />
        )}
      </li>
      <AccordionContent className="pb-0 ">
        <Accordion type="single" collapsible>
          {hasChildren &&
            category.childs?.map((child, index) => (
              <CategoryItem key={index} category={child} layer={layer ? layer + 1 : 1} />
            ))}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const generalLayout = useGlobalStore(state => state.generalLayout);
  const locale = useLocaleStore(s => s.currentLocale);

  const categories = props.categories || generalLayout.header?.categories;

  return (
    <>
      <Sheet>
        <SheetTrigger
          aria-label="Menu"
          className={cn('text-primary', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
        >
          {HEADER_CONFIG?.sidebar?.triggerIcon}
        </SheetTrigger>
        <SheetContent
          aria-describedby={undefined}
          side={props.side}
          className={cn(
            'justify-between overflow-scroll p-0',
            'ea-mobile-sidebar__content',
            {
              'pt-20': !props?.firstSeparator,
            },
            props.className?.content,
          )}
          closeIcon={{
            wrapper: 'right-4 top-4',
            icon: 'text-primary h-6 w-6 font-bold',
          }}
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className={cn('', 'ea-sidebar__first-row')}>
            <LanguageSelect />
            <Button asChild className={cn('ea-sidebar__button')}>
              <Link href={`https://eshop.smako.${locale === 'cs' ? 'cz' : locale}`}>
                <IoMdCart size={16} />
                <p>Eshop</p>
              </Link>
            </Button>
          </div>
          <div className="w-full space-y-4">
            <ul
              className={cn(
                'overflow-scroll',
                'ea-menu-sidebar__list',
                // className?.list,
              )}
            >
              <Accordion type="single" collapsible>
                {categories?.map((category, index) => (
                  <CategoryItem key={index} category={category} />
                ))}
              </Accordion>
            </ul>
            {!!props.menu && (
              <div className="flex flex-col gap-0">
                {props.menu?.map((item, index) => {
                  if (!item.path) {
                    return (
                      <SheetClose key={index} asChild>
                        <span className="flex w-full">
                          <div className={cn('', 'ea-sidebar__menu-item')}>{item.name}</div>
                        </span>
                      </SheetClose>
                    );
                  }

                  const safeHref = getSafeHref(item.path);

                  if (!isRelativeHref(safeHref)) {
                    return (
                      <SheetClose key={index} asChild>
                        <a href={safeHref} className="flex w-full">
                          <div className={cn('', 'ea-sidebar__menu-item')}>{item.name}</div>
                        </a>
                      </SheetClose>
                    );
                  }

                  return (
                    <SheetClose key={index} asChild>
                      <Link href={safeHref} className="flex w-full" prefetch={false}>
                        <div className={cn('', 'ea-sidebar__menu-item')}>{item.name}</div>
                      </Link>
                    </SheetClose>
                  );
                })}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileSidebar;

